import {
  mergeAttributes,
  ReactRenderer,
  type AnyExtension,
} from "@tiptap/react";
import { SuggestionsListHandle } from "./SuggestionsList";
import { SuggestionPopover } from "./SuggestionsPopover";
import type {
  SuggestionKeyDownProps,
  SuggestionOptions,
  SuggestionProps,
} from "@tiptap/suggestion";
import Mention from "@tiptap/extension-mention";

export type GetAutocompleteOptions = (
  query: string,
  selectedItem?: string
) => Promise<string[]> | string[];

export interface AutoCompleteSettings {
  char?: string;
  getAutocompleteOptions?: GetAutocompleteOptions;
  checkShouldKeepType?: (value: string) => boolean;
  showFullValueTooltip?: boolean;
}

export enum KeyCode {
  // @ts-ignore: no-unused-vars
  Escape = "Escape",
  // @ts-ignore: no-unused-vars
  Backspace = "Backspace",
  // @ts-ignore: no-unused-vars
  ArrowUp = "ArrowUp",
  // @ts-ignore: no-unused-vars
  ArrowDown = "ArrowDown",
  // @ts-ignore: no-unused-vars
  Enter = "Enter",
}

let activeSuggestionMenus = 0;

export const isSuggestionMenuOpen = () => activeSuggestionMenus > 0;

const createSuggestions = (
  autoCompleteSettings: AutoCompleteSettings | undefined
): Omit<SuggestionOptions<string>, "editor"> => {
  // Prefix chaining:
  // - Trigger char must always be the first character in the active suggestion text.
  // - Prefix is rendered as plain text right after the trigger (e.g. `@Liverpool FC.`).
  // - The "effective" query passed to getAutocompleteOptions is the suffix the user types
  //   after the prefix (we strip `${prefix}.` from TipTap's query).
  let pendingPrefix: string | null = null;
  const triggerChar = autoCompleteSettings?.char ?? "{";
  const showFullValueTooltip =
    autoCompleteSettings?.showFullValueTooltip ?? false;

  const prefixToken = (prefix: string | null) => (prefix ? `${prefix}.` : "");

  const stripPrefixFromQuery = (rawQuery: string) => {
    const token = prefixToken(pendingPrefix);
    return token && rawQuery.startsWith(token)
      ? rawQuery.slice(token.length)
      : rawQuery;
  };

  const renderTextForPrefix = (prefix: string | null) =>
    `${triggerChar}${prefixToken(prefix)}`;

  const fetchItems = async (rawQuery: string) => {
    const userTypedQuery = stripPrefixFromQuery(rawQuery);
    const suggestedItems = autoCompleteSettings?.getAutocompleteOptions?.(
      userTypedQuery,
      pendingPrefix ?? undefined
    );
    return (await Promise.resolve(suggestedItems)) ?? [];
  };

  const normalizeSelectedValue = (prefix: string | null, rawId: string) => {
    const id = String(rawId ?? "").replace(/^\./, "");
    if (!prefix) {
      return id;
    }
    const prefixWithDot = `${prefix}.`;
    return id.startsWith(prefixWithDot) ? id : `${prefixWithDot}${id}`;
  };

  return {
    char: triggerChar,
    startOfLine: false,
    allowSpaces: false,
    allowedPrefixes: null,
    items: async ({ query }: { query: string }) => {
      return await fetchItems(query);
    },
    render: () => {
      let component: ReactRenderer | null = null;
      let listRef: SuggestionsListHandle | null = null;
      let activeSuggestionProps: SuggestionProps | null = null;

      const updatePopover = async (
        props: SuggestionProps,
        rawQuery: string
      ) => {
        const items = await fetchItems(rawQuery);
        component?.updateProps({
          items,
          command: selectedItemCommand,
          clientRect: props.clientRect,
          searchValue: stripPrefixFromQuery(props.query ?? ""),
          showFullValueTooltip,
        });
      };

      const setPrefix = async (props: SuggestionProps, nextPrefix: string) => {
        pendingPrefix = nextPrefix;

        // Replace the whole suggestion range with `@${prefix}.`
        props.editor
          .chain()
          .focus()
          .insertContentAt(props.range, [
            { type: "text", text: renderTextForPrefix(pendingPrefix) },
          ])
          .scrollIntoView()
          .run();

        await updatePopover(props, prefixToken(pendingPrefix));
      };

      const popPrefixLevel = async (props: SuggestionProps) => {
        if (!pendingPrefix) {
          return;
        }

        pendingPrefix = pendingPrefix.includes(".")
          ? pendingPrefix.slice(0, pendingPrefix.lastIndexOf("."))
          : null;

        props.editor
          .chain()
          .focus()
          .insertContentAt(props.range, [
            { type: "text", text: renderTextForPrefix(pendingPrefix) },
          ])
          .scrollIntoView()
          .run();

        await updatePopover(props, prefixToken(pendingPrefix));
      };

      const selectedItemCommand = async (id: string) => {
        if (!activeSuggestionProps) {
          return false;
        }

        const nextValue = normalizeSelectedValue(pendingPrefix, id);

        // Prefix selection keeps the popover open and rewrites the suggestion text to `@prefix.`
        if (autoCompleteSettings?.checkShouldKeepType?.(nextValue)) {
          await setPrefix(activeSuggestionProps, nextValue);
          return false;
        }

        const value = `{{${nextValue}}}`;
        pendingPrefix = null;

        activeSuggestionProps.editor
          .chain()
          .focus()
          .insertContentAt(activeSuggestionProps.range, [
            { type: "mention", attrs: { id: value, label: value } },
            { type: "text", text: " " },
          ])
          .scrollIntoView()
          .run();

        destroyPopover();
        return true;
      };

      const destroyPopover = () => {
        component?.destroy();
        component = null;
        listRef = null;
      };

      const createPopover = (props: SuggestionProps) => {
        component = new ReactRenderer(SuggestionPopover, {
          props: {
            items: props.items,
            command: selectedItemCommand,
            clientRect: props.clientRect,
            onListRef: (ref: SuggestionsListHandle | null) => {
              listRef = ref;
            },
            searchValue: stripPrefixFromQuery(props.query ?? ""),
            showFullValueTooltip,
          },
          editor: props.editor,
        });
      };

      return {
        // TipTap calls onStart/onUpdate with `loading: true` and empty items
        // before its fetch resolves; wait for the loaded items so the list
        // doesn't flash "No result" on every keystroke
        onStart: (props: SuggestionProps) => {
          activeSuggestionProps = props;
          activeSuggestionMenus += 1;
          // a previous session's renderer may still be referenced; never reuse it
          destroyPopover();
          if (!props.loading) {
            createPopover(props);
          }
        },
        onUpdate(props: SuggestionProps) {
          activeSuggestionProps = props;
          if (props.loading) {
            return;
          }
          if (!component) {
            createPopover(props);
            return;
          }
          component.updateProps({
            items: props.items,
            command: selectedItemCommand,
            clientRect: props.clientRect,
            onListRef: (ref: SuggestionsListHandle | null) => {
              listRef = ref;
            },
            searchValue: stripPrefixFromQuery(props.query ?? ""),
            showFullValueTooltip,
          });
        },
        onKeyDown(props: SuggestionKeyDownProps) {
          if (!activeSuggestionProps) {
            return false;
          }
          if (props.event.key === KeyCode.Escape) {
            destroyPopover();
            pendingPrefix = null;
            activeSuggestionProps = null;
            return true;
          }

          if (props.event.key === KeyCode.Backspace && pendingPrefix) {
            const rawQuery = activeSuggestionProps.query ?? "";
            const effectiveQuery = stripPrefixFromQuery(rawQuery);
            if (!effectiveQuery.length) {
              props.event.preventDefault();
              popPrefixLevel(activeSuggestionProps);
              return true;
            }
          }

          if (
            props.event.key === KeyCode.ArrowUp ||
            props.event.key === KeyCode.ArrowDown ||
            props.event.key === KeyCode.Enter
          ) {
            const handled =
              listRef?.onKeyDown?.({ event: props.event }) ?? false;

            if (!handled) {
              return false;
            }

            props.event.preventDefault();
            return true;
          }
          return false;
        },
        onExit() {
          destroyPopover();
          pendingPrefix = null;
          activeSuggestionProps = null;
          activeSuggestionMenus = Math.max(0, activeSuggestionMenus - 1);
        },
      };
    },
  };
};

export const getMentionExtension = (
  autoCompleteSettings: AutoCompleteSettings | undefined
): AnyExtension | null => {
  const stripTokenBraces = (token: string) =>
    String(token ?? "")
      .replace(/^\s*\{\{/, "")
      .replace(/\}\}\s*$/, "")
      .trim();

  return !!autoCompleteSettings?.getAutocompleteOptions
    ? Mention.configure({
        HTMLAttributes: {
          class: "text-editor-autocomplete",
        },
        deleteTriggerWithBackspace: true,
        renderHTML({ options, node }) {
          const labelToShow = stripTokenBraces(
            node.attrs.label || node.attrs.id
          );
          return [
            "span",
            mergeAttributes(options.HTMLAttributes, node.attrs, {
              class: "text-editor-autocomplete",
            }),
            labelToShow,
          ];
        },
        renderText({ node }) {
          const labelToShow = node.attrs.label || node.attrs.id || "";

          return labelToShow;
        },
        suggestion: createSuggestions(autoCompleteSettings),
      })
    : null;
};
